/**
 * Single source of truth for product data. Must match docs/PRICING_AND_FUNNEL.md.
 */

export const currency = 'EUR'

export const diagnostic = {
  id: 'diagnostic',
  label: 'Diagnostic',
  name: 'The Diagnostic Set',
  price: 65,
  priceDisplay: '€65',
  priceNote: '10ml + 4 × 2ml',
  description: 'Ghost 10ml plus 2ml of each main variant. The system in miniature.',
  contents: [
    'Ghost 10ml (spray)',
    'Predator 2ml (spray)',
    'Intimacy 2ml (oil)',
    'Animal 2ml (oil)',
    'Euphoria 2ml (spray)',
    '€65 credit toward any upgrade',
  ],
  cta: 'Join the Waiting List',
  ctaHref: '/login',
  depositAmount: '€25',
  depositHref: '/login',
  featured: true,
}

export const sovereign = {
  id: 'sovereign',
  label: 'Sovereign',
  name: 'The Sovereign System',
  price: 295,
  priceDisplay: '€295',
  priceNote: '100ml + 2 full-size variants',
  description: 'Ghost 100ml — the foundation — plus two variants of your choice at full size.',
  contents: [
    'Ghost 100ml (spray)',
    'Two full-size variants of your choice',
    'Variant mood cards included',
    'Monolith case — volcanic basalt finish',
    'Free shipping',
  ],
  cta: 'Invitation Only',
  ctaHref: '/login',
  depositAmount: '€45',
  depositHref: '/login',
  featured: false,
}

export const apex = {
  id: 'apex',
  label: 'Apex',
  name: 'The Apex Collection',
  price: 595,
  priceDisplay: '€595',
  priceNote: '100ml + 5 variants + layering guide',
  description: 'Ghost 100ml. Every variant at full size. Aftermath included. Personalized layering guide.',
  contents: [
    'Ghost 100ml (spray)',
    'Predator 20ml (spray)',
    'Euphoria 20ml (spray)',
    'Intimacy 10ml (oil) + Aftermath 3ml (oil)',
    'Animal 10ml (oil)',
    'Personalized layering guide',
    'Monolith case — volcanic basalt finish',
    'Free shipping',
  ],
  cta: 'Invitation Only',
  ctaHref: '/login',
  depositAmount: '€75',
  depositHref: '/login',
  featured: false,
}

export const tiers = [diagnostic, sovereign, apex]

export const standaloneVariants = [
  { name: 'Predator', range: 'Projecting', format: '20ml Spray', price: '€110', color: 'var(--predator)' },
  { name: 'Euphoria', range: 'Projecting', format: '20ml Spray', price: '€110', color: 'var(--euphoria)' },
  { name: 'Intimacy', range: 'Inviting', format: '10ml Oil', price: '€120', color: 'var(--intimacy)' },
  { name: 'Animal', range: 'Inviting', format: '10ml Oil', price: '€120', color: 'var(--animal)' },
]

export const ecosystemProducts = [
  { id: 'canvas', name: 'Canvas', category: 'Preparation', format: '5ml Roller', price: '€28', color: 'var(--ghost)', note: 'Complimentary with select tiers' },
  { id: 'zero', name: 'Zero', category: 'Preparation', format: '50ml Stick', price: '€36', color: 'var(--ghost)' },
  { id: 'silence', name: 'Silence', category: 'Fabric Care', format: '100ml Spray', price: '€32', color: 'var(--ghost)' },
  { id: 'silence-ghost', name: 'Silence + Ghost', category: 'Fabric Care', format: '100ml Spray', price: '€38', color: 'var(--ghost)' },
  { id: 'ghost-linen', name: 'Ghost Linen', category: 'Fabric Care', format: '3 Wool Balls + 5ml Oil', price: '€45', color: 'var(--ghost)' },
  { id: 'ghost-linen-refill', name: 'Ghost Linen Refill', category: 'Fabric Care', format: '5ml Oil', price: '€18', color: 'var(--ghost)' },
  { id: 'ghost-wash', name: 'Ghost Wash', category: 'Body Care', format: '300ml Gel', price: '€30', color: 'var(--ghost)', note: 'Complimentary with Sovereign/Apex. Repurchase: Sovereign/Apex only.', access: 'sovereign+' },
  { id: 'ghost-shampoo', name: 'Ghost Shampoo', category: 'Body Care', format: '250ml', price: '€30', color: 'var(--ghost)', note: 'Fragrance-free. Complimentary with Sovereign/Apex. Repurchase: Sovereign/Apex only.', access: 'sovereign+' },
]

export const discoverySets = [
  { numeral: 'I', name: 'Ghost', tag: 'This is who you are.', format: '10ml Spray', color: 'var(--ghost)' },
  { numeral: 'II', name: 'Predator', tag: 'When you stop pretending.', format: '2ml Spray', color: 'var(--predator)' },
  { numeral: 'III', name: 'Euphoria', tag: 'In a lawless country.', format: '2ml Spray', color: 'var(--euphoria)' },
  { numeral: 'IV', name: 'Intimacy', tag: 'When you let go.', format: '2ml Oil', color: 'var(--intimacy)' },
  { numeral: 'V', name: 'Animal', tag: 'When nobody is watching.', format: '2ml Oil', color: 'var(--animal)' },
]

export const knobVariants = [
  {
    name: 'Aftermath',
    range: 'Hypnotic',
    color: 'var(--aftermath)',
    fill: 8,
    tagline: '"It\'s happening!"',
    body: 'When it all comes together.',
    format: '3ml Oil',
    carrier: 'Squalane + Jojoba',
  },
  {
    name: 'Intimacy',
    range: 'Inviting',
    color: 'var(--intimacy)',
    fill: 22,
    tagline: '"Right about time."',
    body: 'When you let go.',
    format: '10ml Oil',
    carrier: 'Jojoba + Squalane',
  },
  {
    name: 'Animal',
    range: 'Inviting',
    color: 'var(--animal)',
    fill: 38,
    tagline: '"Into the darkness."',
    body: 'When nobody is watching.',
    format: '10ml Oil',
    carrier: 'Jojoba Oil',
  },
  {
    name: 'Ghost',
    range: 'Magnetic',
    color: 'var(--ghost)',
    fill: 55,
    tagline: '"Just the way it is."',
    body: 'This is who you are.',
    format: '100ml Spray',
    carrier: 'Alcohol',
  },
  {
    name: 'Euphoria',
    range: 'Projecting',
    color: 'var(--euphoria)',
    fill: 75,
    tagline: '"It doesn\'t have to end."',
    body: 'In a lawless country.',
    format: '20ml Spray',
    carrier: 'Alcohol',
  },
  {
    name: 'Predator',
    range: 'Projecting',
    color: 'var(--predator)',
    fill: 100,
    tagline: '"The time is now."',
    body: 'When you stop pretending.',
    format: '20ml Spray',
    carrier: 'Alcohol',
  },
]

export const variantDetails = {
  Ghost: {
    tagline: 'This is who you are.',
    philosophy: 'Ghost doesn\'t smell like a fragrance. It replaces your baseline. Iso E Super — the molecule perfumers call "the ghost" — integrates so completely with your skin chemistry that your brain stops distinguishing it from your own scent. To everyone else, you just smell nice. Ambroxan bonds with skin chemistry rather than sitting on top of it, reading as clean, slightly salty warmth that responds to your body heat. Hedione adds jasmine-like radiance and transparency that connects everything without ever registering as perfume — a 2015 NeuroImage study found it activates the human pheromone receptor VN1R1. Methyl Ionone Gamma brings soft violet-iris — smooth, powdery, subliminal. Ethylene Brassylate anchors it all with white musk that smells like warm, clean skin. A bare touch of vanillin rounds it out. The result: when you wear Ghost, nobody thinks "nice cologne." They just never think you smell bad. You smell like a person who\'s always clean, warm, pleasant — not perfumed, just right. Even when you sweat. A smooth, creamy aura that isn\'t noticed as fragrance — it\'s noticed as you. Wearing it is like turning on an enchanting no one can place a finger on. The only way to detect it is absence.',
    occasion: 'Every day. Every room.',
    scentProfile: {
      top: 'Jasmine, Mineral Accord',
      heart: 'Velvet Wood, Violet, Iris',
      base: 'Skin Musk, Amber, Vanilla',
    },
    inci: 'Alcohol Denat., Iso E Super, Hedione (Methyl Dihydrojasmonate), Methyl Ionone Gamma, Ethylene Brassylate, Ambroxan, Ethyl Vanillin. Allergens: Alpha-Isomethyl Ionone (2.03%, from Methyl Ionone Gamma).',
    format: '100ml Spray / 10ml Travel Spray',
    carrier: 'Alcohol',
    concentration: 'Extrait de Parfum (30%)',
    longevity: 'Long-lasting (individual results vary)',
    sillage: 'Intimate-to-moderate — conversation range',
    regulatory: 'EU 1223/2009 (Cosmetic)',
    formulation: {
      version: 'V2.3',
      batchSize: '30ml',
      concentration: '30% Extrait',
      pureOil: '7.87ml (26.2%)',
      cogs: '€16.20',
      shelfLife: '36 months unopened / 12M PAO',
      packaging: 'Dark glass (amber/black), atomizer',
      ingredients: [
        { name: 'Iso E Super', vol: '5.790ml', pctConc: '64.3%', fn: 'Subliminal backbone — ultra-low VP, high LogP' },
        { name: 'Ambroxan 10%', vol: '1.225ml', pctConc: '13.6%', fn: 'Skin-merge anchor, OR7A17' },
        { name: 'Hedione', vol: '0.900ml', pctConc: '10.0%', fn: 'Radiance engine, VN1R1 activation' },
        { name: 'Methyl Ionone Gamma', vol: '0.610ml', pctConc: '6.8%', fn: 'Violet/powdery skin illusion' },
        { name: 'Ethylene Brassylate', vol: '0.450ml', pctConc: '5.0%', fn: 'Skin-deepening macrocyclic musk' },
        { name: 'Ethyl Vanillin 1%', vol: '~0.025ml', pctConc: '0.3%', fn: 'Sub-threshold warmth modifier' },
        { name: "Perfumer's Alcohol", vol: '~21.0ml', pctConc: '—', fn: 'Carrier' },
      ],
    },
    oneLiners: [
      'This is who you are.',
      '"You smell... good" — not "What are you wearing?"',
      'The fragrance equivalent of having exceptional genetics.',
    ],
    pairing: [
      'Ghost goes on FIRST, always. Pulse points — neck, wrists, chest.',
      'Layers under all other variants (Predator, Animal, Intimacy, Euphoria).',
      'Ghost + Predator: Ghost on pulse points (intimate warmth), Predator on chest/neck (social projection).',
      'Ghost + Animal: Ghost\'s woody aura at arm\'s length, Animal\'s feral depth at skin contact.',
      'Ghost + Intimacy: Ghost\'s woody skin + Intimacy\'s warm cashmere cocoon.',
      'Ghost + Euphoria: Ghost grounds, Euphoria lifts.',
    ],
    evolution: [
      { phase: 'Flash-off', time: '0–10 min', desc: 'Alcohol dissipates, molecules embed in skin lipids.' },
      { phase: 'Full Expression', time: '10 min–4 hr', desc: 'Iso E Super + Ambroxan fully expressed. Woody-skin character, ambiguous as fragrance.' },
      { phase: 'Flicker Zone', time: '4–8 hr', desc: 'IES persists through perceptual flickering and anosmia — others smell it when you can\'t.' },
      { phase: 'Skin Imprint', time: '8–16 hr', desc: 'Deep skin imprint, barely detectable at distance. Intimate range only.' },
    ],
  },
  Predator: {
    tagline: 'Presence without permission.',
    philosophy: 'Ghost\'s skeleton — hedione (jasmine, amplified), ambroxan (warm amber), violet (alpha-ionone) — is running through every layer of this. The twist: everything louder. Hedione HC intensifies the jasmine radiance to room-filling potency — the same molecule at higher purity, filling rooms the way regular hedione fills conversations. Timbersilk replaces Ghost\'s transparent Iso E Super with dense, creamy wood that people perceive consistently — no anosmia, no flickering. Cetalox adds a massive ambergris-mineral backbone that extends the dry-down for hours. Norlimbanol threads dry driftwood depth through the heart — sun-bleached, dark, slightly feral — the edge that makes the authority feel earned, not announced. Ghost decided to take the room. Not asking — commanding. Smells like walking in and the room rearranging itself around you. Ghost with authority.',
    occasion: 'When you dress up and the room fills.',
    scentProfile: {
      top: 'Radiant Jasmine, Cool Air',
      heart: 'Dense Wood, Driftwood, Violet',
      base: 'Mineral Amber, Warm Skin',
    },
    inci: 'Alcohol Denat., Timbersilk, Hedione (Methyl Dihydrojasmonate), Ambroxan, Hedione HC (Methyl Dihydrojasmonate High-Cis), Cetalox, Alpha-Ionone, Norlimbanol, BHT.',
    format: '20ml Spray / 2ml Discovery Spray',
    carrier: 'Alcohol',
    concentration: 'Extrait de Parfum (~30%)',
    longevity: 'Long-lasting (individual results vary)',
    sillage: 'Heavy — fills a room',
    regulatory: 'EU 1223/2009 (Cosmetic)',
    formulation: {
      version: 'V2.2',
      batchSize: '10ml',
      concentration: '~38.6% Extrait',
      pureOil: '2.640ml (26.4%)',
      cogs: '<€8',
      shelfLife: '36 months unopened / 12M PAO',
      packaging: 'Dark glass (amber/black), atomizer',
      ingredients: [
        { name: 'Ambroxan 10%', vol: '1.350ml', pctConc: '35.0%', fn: 'Skin-merge anchor, OR7A17 — 3x reinforced' },
        { name: 'Timbersilk', vol: '1.050ml', pctConc: '27.2%', fn: 'Structural backbone — reliable dense wood' },
        { name: 'Hedione', vol: '0.660ml', pctConc: '17.1%', fn: 'Projection engine — VP 0.1–0.2 Pa' },
        { name: 'Hedione HC', vol: '0.300ml', pctConc: '7.8%', fn: 'Enhanced projection — ~16x lower threshold' },
        { name: 'Cetalox', vol: '0.240ml', pctConc: '6.2%', fn: 'Longevity anchor — dry amber depth' },
        { name: 'Alpha Ionone', vol: '0.180ml', pctConc: '4.7%', fn: 'Violet/powdery bridge' },
        { name: 'Norlimbanol', vol: '0.075ml', pctConc: '1.9%', fn: 'Dark driftwood depth, VP bridge 0.063 Pa' },
        { name: 'BHT 2%', vol: '0.10ml', pctConc: '—', fn: 'Antioxidant (Alpha-Ionone protection)' },
        { name: "Perfumer's Alcohol", vol: '6.045ml', pctConc: '—', fn: 'Carrier' },
      ],
    },
    oneLiners: [
      'Presence without permission.',
      'Ghost that stopped being shy.',
      'Same DNA. Different volume knob.',
    ],
    pairing: [
      'Predator layers on top of Ghost for dual-range effect.',
      'Ghost on pulse points (inviting warmth at 2–3 feet), Predator on chest/neck (commanding presence at 3–6 feet).',
      'Ghost + Predator + Euphoria ("the host") — commands room AND makes it fun.',
    ],
    evolution: [
      { phase: 'Presence', time: '0–30 min', desc: 'Hedione dominates 3–6 ft zone. Radiant warm wood, commanding. Timbersilk + Ambroxan + Cetalox provide close-range skin character.' },
      { phase: 'Warm Retreat', time: '30 min–4 hr', desc: 'Hedione exhausting surface supply. Cetalox takes over — dry amber depth. Detectable at 1–2 feet.' },
      { phase: 'Settling', time: '4–8 hr', desc: 'Deeper, drier, closer. Hedione heavily reduced.' },
      { phase: 'Skin Imprint', time: '8–14+ hr', desc: 'Dry amber-wood skin scent at intimate range. Cetalox + residual Timbersilk + Ambroxan persist.' },
    ],
  },
  Intimacy: {
    tagline: 'When you let go.',
    philosophy: 'Starts from Ghost\'s invisible skin-scent foundation — hedione\'s jasmine brightness, ambroxan\'s warm amber, vanilla\'s soft sweetness — then twists it inward. Where Ghost reads as expensive skin from a distance, Intimacy takes that same warmth and wraps it in skin. Four layered lactones — peach (delta-decalactone), coconut (gamma-decalactone), cream (gamma-undecalactone), and the coumarinic milk of methyl laitone binding them together. Hedione HC amplifies Ghost\'s jasmine into luminous radiance that settles into cashmeran warmth and powdery white musk (muscenone). Australian sandalwood adds milky, creamy depth. A trace of orris butter brings powdery violet-root elegance to the drydown — the most expensive natural in perfumery, used here because nothing else does what it does. Ghost decided to let go. Not projecting, not performing — just softening, opening, letting someone all the way in. The same DNA turned softer, closer, meant for skin on skin. Ghost with its guard down.',
    occasion: 'One on one. Date night. Close.',
    scentProfile: {
      top: 'Luminous Jasmine, Freshness',
      heart: 'Cashmere, Cream, Warm Musk, Sandalwood',
      base: 'White Musk, Orris, Mineral, Vanilla',
    },
    inci: 'Simmondsia Chinensis (Jojoba) Seed Oil, Squalane, Cashmeran, Isopropyl Myristate, Muscenone, Hedione HC (Methyl Dihydrojasmonate High-Cis), Helvetolide, Sandalwood Oil (Santalum spicatum), Delta-Decalactone, Gamma-Decalactone, Gamma-Undecalactone, Methyl Laitone, Orris Butter (Iris pallida), Ambroxan, Ethyl Vanillin, Dipropylene Glycol, Tocopherol, Parfum (Fragrance). Allergens: Linalool.',
    format: '10ml Oil / 2ml Discovery Oil',
    carrier: 'Jojoba + Squalane',
    concentration: 'Perfume Oil (~25%)',
    longevity: 'Long-lasting (individual results vary)',
    sillage: 'Intimate — skin-close',
    regulatory: 'EU 1223/2009 (Cosmetic)',
    formulation: {
      version: 'V2.2',
      batchSize: '10ml',
      concentration: '25%',
      pureOil: '1.768ml (17.7%)',
      cogs: '€5–8',
      shelfLife: '36 months unopened / 12M PAO',
      packaging: '10ml amber glass, rollerball',
      ingredients: [
        { name: 'Cashmeran', vol: '0.350ml', pctConc: '14.0%', fn: 'Backbone — warm cashmere (IFRA Cat 4: 3.50% vs 3.80% limit — 8% safety margin)' },
        { name: 'Ambroxan 10%', vol: '0.375ml', pctConc: '15.0%', fn: 'Molecular bridge to Ghost, OR7A17' },
        { name: 'Muscenone', vol: '0.320ml', pctConc: '12.8%', fn: 'Body-heat-activated musk, OR5AN1' },
        { name: 'Hedione HC', vol: '0.250ml', pctConc: '10.0%', fn: 'Luminosity, creamier jasmine' },
        { name: 'Ethyl Vanillin 10%', vol: '0.200ml', pctConc: '8.0%', fn: 'Sub-threshold warmth (~0.2% pure EV)' },
        { name: 'Helvetolide', vol: '0.225ml', pctConc: '9.0%', fn: 'Clean musk support' },
        { name: 'Delta-Decalactone', vol: '0.200ml', pctConc: '8.0%', fn: 'Creamy skin lactone' },
        { name: 'Sandalwood Oil (S. spicatum)', vol: '0.200ml', pctConc: '8.0%', fn: 'Milky woody depth' },
        { name: 'Methyl Laitone 10% (TEC)', vol: '0.100ml', pctConc: '4.0%', fn: 'Lactone harmonizer/binder' },
        { name: 'Gamma-Decalactone', vol: '0.050ml', pctConc: '2.0%', fn: 'Peach-skin warmth' },
        { name: 'Orris Butter (Iris pallida)', vol: '0.038ml', pctConc: '1.5%', fn: 'Powdery violet-root elegance' },
        { name: 'Gamma-Undecalactone', vol: '0.037ml', pctConc: '1.5%', fn: 'Waxy skin warmth' },
        { name: 'DPG', vol: '0.125ml', pctConc: '5.0%', fn: 'Processing aid' },
        { name: 'Mixed Tocopherols 1%', vol: '0.50ml', pctConc: '—', fn: 'Antioxidant' },
        { name: 'Jojoba Oil', vol: '6.050ml', pctConc: '—', fn: 'Primary carrier' },
        { name: 'Squalane', vol: '0.630ml', pctConc: '—', fn: 'Ultra-light secondary carrier' },
        { name: 'IPM', vol: '0.320ml', pctConc: '—', fn: 'Penetration enhancer' },
      ],
    },
    oneLiners: [
      'When you let go.',
      'Not seduction. Not romance. Just: stay.',
      'Same DNA. Closest distance.',
    ],
    pairing: [
      'Oil-based roller — no flash-off, no projection burst. Discovered during closeness.',
      'Applied to neck, behind ears, inner wrists, chest (intimate zones).',
      'Intimacy first (oil to skin), wait 5–10 min, Ghost second (spray over oil layer).',
      'Perfect pairing with Aftermath: Intimacy is warmth that envelops; Aftermath is depth discovered at closest range.',
      'Ghost + Intimacy shares Ambroxan "molecular mortar" between layers.',
    ],
    evolution: [
      { phase: 'Warming', time: '0–5 min', desc: 'Oil warming on skin, subliminal warmth.' },
      { phase: 'Unfolding', time: '5–30 min', desc: 'Warm skin, soft woody quality, subliminal sweetness.' },
      { phase: 'Peak Zone', time: '30 min–2 hr', desc: 'Full "warm cashmere" impression. Creamy, skin-like. Discovered at 0–2 feet.' },
      { phase: 'Sustain', time: '2–6 hr', desc: 'Slightly reduced intensity but stable. Cashmeran + musks persist.' },
      { phase: 'Pillow Scent', time: '6–14 hr', desc: 'Pure Cashmeran + musk base at skin-contact range. Deeply intimate.' },
    ],
    companion: {
      name: 'Aftermath',
      tagline: 'When it all comes together.',
      description: 'The final layer. Apply Aftermath over Intimacy when the distance closes to zero. 3ml oil — the smallest bottle for the biggest moment.',
      notes: 'Warm Musk, Cashmere, Mineral Amber, Cream',
      format: '3ml Oil',
    },
  },
  Animal: {
    tagline: 'When nobody is watching.',
    philosophy: 'Ghost\'s full DNA is here — Iso E Super (velvet wood), hedione (jasmine), ambroxan (warm amber), violet (methyl ionone), vanilla — the same invisible skin scent as the foundation. The twist: safraleine opens with warm saffron-leather heat, cumin aldehyde adds dry, skin-like musk, and then civetone and indole push it raw and animalic — warm body, not perfume. Ghost\'s woody-violet middle is still there, softening the edges, while the amber and vanilla base holds everything together. Ghost gone raw and wild. The civilized layer ripped off — what\'s underneath is warm, feral, unfiltered. Smells like warm skin with an edge — something animal under the surface. Ghost unchained.',
    occasion: 'One on one. Behind closed doors.',
    scentProfile: {
      top: 'Saffron-Leather, Warm Skin, Cumin',
      heart: 'Woody Amber, Warm Jasmine, Violet',
      base: 'Raw Musk, Mineral Amber, Vanilla',
    },
    inci: 'Simmondsia Chinensis (Jojoba) Seed Oil, Iso E Super, Hedione (Methyl Dihydrojasmonate), Safraleine, Methyl Ionone Gamma, Civetone, Ambroxan, Indole, Cumin Aldehyde, Ethyl Vanillin, Tocopherol, Triethyl Citrate, Parfum (Fragrance). Allergens: Alpha-Isomethyl Ionone (1.25%, from Methyl Ionone Gamma).',
    format: '10ml Oil / 2ml Discovery Oil',
    carrier: 'Jojoba Oil',
    concentration: 'Perfume Oil (~20%)',
    longevity: 'Long-lasting (individual results vary)',
    sillage: 'Moderate — draws people in',
    regulatory: 'EU 1223/2009 (Cosmetic)',
    formulation: {
      version: 'V2.1',
      batchSize: '10ml',
      concentration: '20%',
      pureOil: '1.323ml (13.2%)',
      cogs: '<€5',
      shelfLife: '36 months unopened / 12M PAO',
      packaging: '10ml amber glass, rollerball/dabber',
      ingredients: [
        { name: 'Iso E Super', vol: '0.665ml', pctConc: '33.25%', fn: 'Ghost DNA backbone — woody ambiguity' },
        { name: 'Ambroxan 10%', vol: '0.375ml', pctConc: '18.75%', fn: 'DNA anchor, OR7A17' },
        { name: 'Indole 10% (TEC)', vol: '0.250ml', pctConc: '12.5%', fn: 'Jasmine-body bridge' },
        { name: 'Safraleine', vol: '0.200ml', pctConc: '10.0%', fn: 'Saffron-leather character' },
        { name: 'Hedione', vol: '0.160ml', pctConc: '8.0%', fn: 'Radiance at intimate range' },
        { name: 'Methyl Ionone Gamma', vol: '0.125ml', pctConc: '6.25%', fn: 'Violet/powdery bridge' },
        { name: 'Civetone', vol: '0.100ml', pctConc: '5.0%', fn: 'Signature — raw animalic musk' },
        { name: 'Cumin Aldehyde 10% (TEC)', vol: '0.100ml', pctConc: '5.0%', fn: 'Perceptible warm skin note' },
        { name: 'Ethyl Vanillin 1%', vol: '0.025ml', pctConc: '1.25%', fn: 'Sub-threshold warmth' },
        { name: 'Mixed Tocopherols 1%', vol: '0.50ml', pctConc: '—', fn: 'Antioxidant (Indole protection)' },
        { name: 'Jojoba Oil', vol: '7.500ml', pctConc: '—', fn: 'Carrier' },
      ],
    },
    oneLiners: [
      'When nobody is watching.',
      'The version of you that exists at 2am.',
      'Not dirty. Not clean. Alive.',
    ],
    pairing: [
      'Oil-based (jojoba) — operates at intimate range (0–2 feet), discovered by proximity.',
      'Applied to lower neck, chest, inner wrists — 15–30 min before.',
      'Ghost + Animal: Most seamless layering combination — they share most DNA (IES, Ambroxan, Hedione, Methyl Ionone, Ethyl Vanillin).',
      'Ghost provides 2–3 foot ambiguity layer; Animal provides skin-contact depth.',
      '"Ghost with teeth — but the teeth are only felt at close range."',
    ],
    evolution: [
      { phase: 'Warm-up', time: '0–60 min', desc: 'No alcohol flash-off. Oil sits on skin. Cumin Aldehyde and Hedione emerge — warm, slightly sweaty-floral at intimate range.' },
      { phase: 'Reveal', time: '1–6 hr', desc: 'Iso E Super expressing. Civetone emerges, animalic character becomes dominant. Safraleine\'s saffron-leather provides texture. 0–18 inches.' },
      { phase: 'Skin Imprint', time: '6–24+ hr', desc: 'Civetone + Ambroxan + residual IES persist. Warm, musky, slightly sweet, deeply skin-like. Skin-contact range only.' },
    ],
  },
  Euphoria: {
    tagline: 'In a lawless country.',
    philosophy: 'Ghost\'s velvet wood (Iso E Super), jasmine brightness (hedione), and warm skin amber (ambroxan) are all here — this is Ghost underneath. The twist: real Calabrian bergamot opens bright, sparkling, slightly bitter. Hedione HC amplifies Ghost\'s jasmine into full radiance that bridges to Jasmine Sambac absolute\'s narcotic, tea-like floral heart. Cetalox anchors everything with mineral ambergris depth. Timbersilk adds a dense woody backbone that holds the sweetness in place for hours. Ethyl maltol brings caramelized sweetness over the familiar woody warmth. The base is layered: benzoin resinoid for balsamic warmth, doubled ambroxan for lasting skin-merge, coumarin for tonka-hay sweetness, labdanum for dark amber depth, vanilla to round it out. Ghost decided to go out. The outgoing version — bright, magnetic, impossible to ignore. Predator commands a room with authority. Euphoria fills it with energy. Smells like the night pulling you forward — sweet but complex, warm but relentless. Ghost with momentum.',
    occasion: 'Going out. The night that starts with intent.',
    scentProfile: {
      top: 'Calabrian Bergamot, Bright Citrus, Clean Air',
      heart: 'Jasmine Sambac, Caramel, Woody Amber',
      base: 'Benzoin, Tonka, Balsamic Vanilla, Dark Amber',
    },
    inci: 'Alcohol Denat., Iso E Super, Ambroxan, Ethyl Maltol, Hedione HC (Methyl Dihydrojasmonate High-Cis), Benzoin Resinoid, Cetalox (Ambrocenide), Timbersilk, Limonene, Linalool, Ethyl Vanillin, Bergamot Oil FCF (Citrus bergamia), Labdanum Resinoid (Cistus ladanifer), Coumarin, Jasmine Sambac Absolute (Jasminum sambac), BHT. Allergens: Limonene, Linalool, Coumarin, Benzyl benzoate (from Benzoin Resinoid), Benzyl alcohol (from Benzoin Resinoid).',
    format: '20ml Spray / 2ml Discovery Spray',
    carrier: 'Alcohol',
    concentration: 'Extrait de Parfum (~28%)',
    longevity: 'Long-lasting (individual results vary)',
    sillage: 'Heavy — fills a room',
    regulatory: 'EU 1223/2009 (Cosmetic)',
    formulation: {
      version: 'V2.1',
      batchSize: '10ml',
      concentration: '~28% Extrait',
      pureOil: '1.815ml (18.2%)',
      cogs: '<€8',
      shelfLife: '36 months unopened / 12M PAO',
      packaging: 'Dark glass (amber/black), atomizer',
      ingredients: [
        { name: 'Bergamot Oil FCF', vol: '0.100ml', pctConc: '3.6%', fn: 'Sparkling citrus complexity' },
        { name: 'Limonene', vol: '0.150ml', pctConc: '5.4%', fn: 'Citrus burst — VP 200 Pa entrance energy' },
        { name: 'Ethyl Maltol 10%', vol: '0.333ml', pctConc: '11.9%', fn: 'OISE cross-modal sweetness engine' },
        { name: 'Iso E Super', vol: '0.444ml', pctConc: '15.9%', fn: 'Subliminal woody backbone, flicker' },
        { name: 'Hedione HC', vol: '0.278ml', pctConc: '9.9%', fn: 'Radiance, jasmine, VN1R1' },
        { name: 'Jasmine Sambac Abs', vol: '0.030ml', pctConc: '1.1%', fn: 'Narcotic floral heart' },
        { name: 'Ambroxan 10%', vol: '0.444ml', pctConc: '15.9%', fn: 'DNA anchor — doubled, OR7A17' },
        { name: 'Benzoin 50%', vol: '0.278ml', pctConc: '9.9%', fn: 'Balsamic depth, resinous fixative' },
        { name: 'Ethyl Vanillin 10%', vol: '0.111ml', pctConc: '4.0%', fn: 'Memory encoding warmth' },
        { name: 'Linalool', vol: '0.111ml', pctConc: '4.0%', fn: 'Fresh top note' },
        { name: 'Cetalox', vol: '0.222ml', pctConc: '7.9%', fn: 'Longevity anchor — ambergris mineral' },
        { name: 'Timbersilk', vol: '0.167ml', pctConc: '6.0%', fn: 'Marathon backbone — dense creamy wood' },
        { name: 'Coumarin', vol: '0.040ml', pctConc: '1.4%', fn: 'Tonka-hay sweetness' },
        { name: 'Labdanum 50%', vol: '0.092ml', pctConc: '3.3%', fn: 'Dark balsamic amber depth' },
        { name: 'BHT 2%', vol: '0.10ml', pctConc: '—', fn: 'Antioxidant (Limonene/Linalool protection)' },
        { name: "Perfumer's Alcohol", vol: '7.100ml', pctConc: '—', fn: 'Carrier' },
      ],
    },
    oneLiners: [
      'In a lawless country.',
      'Ghost makes you smell like who you are. Euphoria makes you smell like who you\'d be if you weren\'t afraid.',
      'Same person. Different permission.',
    ],
    pairing: [
      'Always worn over Ghost — Ghost provides foundation, Euphoria layers on top.',
      'Ghost (2 sprays) → wait 5 min → Euphoria (2–3 sprays neck, wrists).',
      'Ghost + Predator + Euphoria ("the host") — commands room AND makes it fun.',
      'Ghost + Animal + Euphoria ("feral joy") — Animal\'s rawness + Euphoria\'s warmth radiating.',
    ],
    evolution: [
      { phase: 'Burst', time: '0–15 min', desc: 'Limonene + Linalool + alcohol flash. Bright, sweet, exciting. Projected at 4–8 feet.' },
      { phase: 'Sweet Cloud', time: '15 min–2 hr', desc: 'Ethyl Maltol sustained, Hedione radiance, Timbersilk warmth. Warm sweet diffusion, inviting cloud.' },
      { phase: 'Deep Warmth', time: '2–6 hr', desc: 'Benzoin, Iso E Super, Timbersilk, Cetalox emerging. Balsamic, flickering, dense woody sweetness.' },
      { phase: 'Skin Memory', time: '6–14 hr', desc: 'Cetalox, Ambroxan, ethyl vanillin trace. Mineral amber, sweet memory, Ghost DNA emerges.' },
    ],
  },
  Aftermath: {
    tagline: 'When it all comes together.',
    philosophy: 'Ghost stripped to its warmest bones. Hedione (Ghost\'s jasmine brightness) and ambroxan (Ghost\'s warm skin amber) are the connective tissue — the same molecules that make Ghost feel like expensive skin. The twist: muscone — the real musk molecule — pushes that skin warmth to its limit. Cashmeran wraps it in cozy cashmere. Delta-decalactone adds creamy, peachy softness. Indole adds a hint of raw floral edge that keeps it from going clean-laundry. Ghost decided to disappear into someone. The same invisible warmth concentrated until there\'s nothing left but heat and softness — no you, no them, just warmth. Ghost dissolving.',
    occasion: 'When there\'s nothing left to take off.',
    scentProfile: {
      top: 'Jasmine Brightness, Soft Trace',
      heart: 'Warm Musk, Cashmere',
      base: 'Mineral Amber, Cream, Warmth',
    },
    inci: 'Squalane, Simmondsia Chinensis (Jojoba) Seed Oil, Muscone, Isopropyl Myristate, Cashmeran, Ambroxan, Ethylene Brassylate, Hedione HC (Methyl Dihydrojasmonate High-Cis), Hedione (Methyl Dihydrojasmonate), Indole, Linalool, Delta-Decalactone, Gamma-Undecalactone, Tocopherol, Triethyl Citrate, Parfum (Fragrance). Allergens: Linalool.',
    format: '3ml Oil',
    carrier: 'Squalane + Jojoba',
    concentration: 'Perfume Oil (~20%)',
    longevity: 'Long-lasting (individual results vary)',
    sillage: 'Hypnotic — skin only',
    warning: 'Oil-based formula — not compatible with latex. Compatible with polyurethane and polyisoprene.',
    regulatory: 'EU 1223/2009 (Cosmetic)',
    formulation: {
      version: 'V2.1',
      batchSize: '3ml',
      concentration: '20%',
      pureOil: '0.483ml (16.1%)',
      cogs: '<€5',
      shelfLife: '36 months unopened / 12M PAO',
      packaging: '3ml amber glass, dropper cap',
      ingredients: [
        { name: 'Muscone', vol: '0.150ml', pctConc: '25.0%', fn: 'Signature warm skin musk, body-heat activated, OR5AN1' },
        { name: 'Cashmeran', vol: '0.090ml', pctConc: '15.0%', fn: 'Continuity from Intimacy — warm tactile quality' },
        { name: 'Ambroxan 10%', vol: '0.090ml', pctConc: '15.0%', fn: 'DNA anchor, mineral-salt skin, OR7A17' },
        { name: 'Ethylene Brassylate', vol: '0.060ml', pctConc: '10.0%', fn: 'VP bridge — creamy macrocyclic musk' },
        { name: 'Hedione HC', vol: '0.045ml', pctConc: '7.5%', fn: 'VN1R1 activation, luminosity' },
        { name: 'Hedione', vol: '0.045ml', pctConc: '7.5%', fn: 'Radiance, prevents musky density' },
        { name: 'Indole 10% (TEC)', vol: '0.040ml', pctConc: '6.7%', fn: 'Warm-body character' },
        { name: 'Linalool', vol: '0.030ml', pctConc: '5.0%', fn: 'Clean fresh opening' },
        { name: 'Delta-Decalactone', vol: '0.030ml', pctConc: '5.0%', fn: 'Skin-warmth lactone' },
        { name: 'Gamma-Undecalactone', vol: '0.020ml', pctConc: '3.3%', fn: 'Peach skin lactone' },
        { name: 'Squalane', vol: '1.400ml', pctConc: '—', fn: 'Primary carrier' },
        { name: 'Mixed Tocopherols 1%', vol: '0.150ml', pctConc: '—', fn: 'Antioxidant' },
        { name: 'Jojoba Oil', vol: '0.750ml', pctConc: '—', fn: 'Secondary carrier' },
        { name: 'IPM', vol: '0.100ml', pctConc: '—', fn: 'Penetration enhancer' },
      ],
    },
    oneLiners: [
      'When it all comes together.',
      'The smallest bottle. The closest distance. The biggest moment.',
      'Applied to the places only one person discovers.',
    ],
    pairing: [
      'Oil dropper (3ml) — 3–4 drops to inner thighs, lower abdomen, optional chest.',
      'Applied 15–30 min before anticipated intimacy.',
      'Part of complete intimate stack: Ghost (daily spray) → Intimacy (evening oil) → Aftermath (preparation drops).',
      'Creates continuous scent gradient from 2–3 feet (Ghost) → intimate zones → skin-contact depth (Aftermath).',
      'Bridge from Intimacy: both contain Cashmeran for continuity from neck to most intimate zone.',
    ],
    evolution: [
      { phase: 'Warming', time: '0–15 min', desc: 'Oil warming on skin, molecules dissolving into sebum layer. Minimal scent at distance.' },
      { phase: 'Integration', time: '15–30 min', desc: 'Full integration. Muscone and Cashmeran detectable at skin-contact range.' },
      { phase: 'Arousal Gate', time: 'During arousal', desc: 'Skin temp rises ~4°C — Muscone VP increases ~1.5x. Temperature-gated activation.' },
      { phase: 'Active', time: 'During contact', desc: 'Friction releases stored molecules in bursts. Scent intensifies with every physical interaction.' },
      { phase: 'Aftermath', time: '6–12+ hr', desc: 'Muscone + Ambroxan persist on skin. Warm, musky, skin-like — lingers on bodies and sheets.' },
    ],
  },
}

export const ecosystemDetails = {
  Canvas: {
    tagline: 'Before Ghost, there was noise.',
    philosophy: 'Your skin has a scent signature — sweat, bacteria, last night\'s fragrance, today\'s moisturizer. Every layer interferes. Canvas is a molecular reset. Squalane seals the surface. Jojoba mimics your skin\'s own oils so nothing fights back. Triethyl citrate redirects the bacteria that turn sweat into odor — not by killing them, but by changing what they produce. Isopropyl myristate opens the pathway for whatever comes next. Five seconds. Clean slate. Now Ghost has nothing to compete with.',
    use: 'Apply to pulse points before fragrance. Wrists, neck, behind ears.',
    inci: 'Squalane, Simmondsia Chinensis (Jojoba) Seed Oil, Isopropyl Myristate, Triethyl Citrate, Tocopherol.',
    format: '5ml Roller',
    note: 'Complimentary with select tiers.',
    regulatory: 'EU 1223/2009 (Cosmetic)',
  },
  Zero: {
    tagline: 'Your body. Nothing else.',
    philosophy: 'Most deodorants mask odor with fragrance. That\'s noise on top of noise. Zero eliminates odor at the source — magnesium hydroxide shifts your skin\'s pH so the bacteria that cause body odor can\'t metabolize. Zinc ricinoleate traps whatever gets through. Triethyl citrate redirects what\'s left. No aluminum. No fragrance. No baking soda burn. Just chemistry that works with your body instead of against it. The result: you smell like nothing. Which means Ghost smells like everything.',
    use: 'Apply to clean, dry underarms. Allow 30 seconds to absorb before dressing.',
    inci: 'Cocos Nucifera (Coconut) Oil, Butyrospermum Parkii (Shea) Butter, Maranta Arundinacea Root Powder, Euphorbia Cerifera (Candelilla) Wax, Magnesium Hydroxide, Zinc Ricinoleate, Charcoal Powder, Triethyl Citrate, Tocopherol.', // Charcoal Powder = Binchotan (Kishu white charcoal, activated carbon) — sourced as binchotan charcoal powder
    format: '50ml Stick',
    warning: 'Deodorant only — not an antiperspirant. Contains no aluminum.',
    regulatory: 'EU 1223/2009 (Cosmetic — deodorant)',
  },
  Silence: {
    tagline: 'What your clothes remember, erased.',
    philosophy: 'Fabric holds scent longer than skin — yesterday\'s cooking, last week\'s cologne, the gym bag in the trunk. Cyclodextrin doesn\'t mask these odors. It cage-traps the molecules inside ring-shaped cavities so they physically cannot reach your nose. Zinc ricinoleate catches what\'s left. One spray, sixty seconds, your clothes smell like they just came off the line. Silence before Ghost means Ghost is the only voice in the room.',
    use: 'Spray on fabric from 20cm distance. Allow 60 seconds to dry.',
    inci: 'Aqua (Water), Alcohol Denat., Hydroxypropyl Beta-Cyclodextrin, Zinc Ricinoleate, Polysorbate 20, Phenoxyethanol.',
    format: '100ml Spray',
    regulatory: 'EU 648/2004 (Detergent)',
  },
  'Silence + Ghost': {
    tagline: 'Silence that remembers.',
    philosophy: 'Everything Silence does — neutralize, erase, reset — plus a whisper of Ghost woven into the fabric. Iso E Super and Ambroxan at trace levels, so low they don\'t register as fragrance on their own. Your shirt doesn\'t smell like cologne. It smells like you already had something on. The cyclodextrin traps competing odors while leaving Ghost\'s molecules free to integrate with the textile. Clean slate with a memory.',
    use: 'Spray on fabric from 20cm distance. Allow 60 seconds to dry.',
    inci: 'Aqua (Water), Alcohol Denat., Hydroxypropyl Beta-Cyclodextrin, Zinc Ricinoleate, Polysorbate 20, Phenoxyethanol, Tetramethyl Acetyloctahydronaphthalenes (Iso E Super), Dodecahydro-3a,6,6,9a-Tetramethylnaphtho[2,1-b]furan (Ambroxan).',
    format: '100ml Spray',
    regulatory: 'EU 648/2004 (Detergent)',
  },
  'Ghost Linen': {
    tagline: 'Ghost lives in the fabric.',
    philosophy: 'Three hand-felted New Zealand wool balls. Each one carries Ghost\'s molecular core — Iso E Super and Ambroxan in squalane carrier. Every dryer cycle deposits a trace into your sheets, your shirts, your towels. Not perfume on fabric — warmth in fabric. Nobody knows why your laundry smells so good. You don\'t tell them. Re-apply oil every two to three loads. Refill when the warmth fades.',
    use: 'Apply 3–5 drops of oil to each ball. Place all three in dryer with laundry. Re-apply every 2–3 loads.',
    materials: '100% New Zealand wool (hand-felted, 7.5cm diameter)',
    inci: 'Squalane, Tetramethyl Acetyloctahydronaphthalenes (Iso E Super), Dodecahydro-3a,6,6,9a-Tetramethylnaphtho[2,1-b]furan (Ambroxan), Tocopherol.',
    format: '3 × Wool Dryer Balls + 5ml Refill Oil',
    regulatory: 'EU 1223/2009 (Cosmetic — oil leave-on)',
  },
  'Ghost Linen Refill': {
    tagline: 'The warmth comes back.',
    philosophy: 'The same molecular signature that makes Ghost disappear into skin — now disappearing into fabric. 5ml of Iso E Super and Ambroxan in squalane carrier. No alcohol, because dryer heat would erase it. Oil survives the cycle, bonds with the textile, and keeps giving. Three to five drops per ball. The warmth builds over time — each load layering on the last until your closet has its own quiet atmosphere.',
    use: 'Apply 3–5 drops per wool ball before each dryer cycle.',
    inci: 'Squalane, Tetramethyl Acetyloctahydronaphthalenes (Iso E Super), Dodecahydro-3a,6,6,9a-Tetramethylnaphtho[2,1-b]furan (Ambroxan), Tocopherol.',
    format: '5ml Oil',
    regulatory: 'EU 1223/2009 (Cosmetic)',
  },
  'Ghost Wash': {
    tagline: 'The first layer is the shower.',
    philosophy: 'Every body wash you\'ve ever used puts competing scent on your skin. Ghost Wash doesn\'t compete — it pre-loads the foundation. Sodium Cocoyl Isethionate — the mildest effective surfactant in cosmetic chemistry — cleans without stripping the lipid layer your fragrance needs to bind to. Ceramides and cholesterol replenish what washing takes. Glycerin holds moisture. pH 5.0 preserves your skin\'s acid mantle. And Ghost\'s molecular core — Iso E Super and Ambroxan — deposits into your skin\'s lipid layer during the wash because both molecules are highly lipophilic (logP >4.5). They stay when the water leaves. By the time you step out, your skin isn\'t just clean. It\'s already wearing Ghost.',
    use: 'Use daily in shower. Lather, allow brief contact, rinse. Follow with Canvas on pulse points, then Ghost.',
    inci: 'Aqua (Water), Sodium Cocoyl Isethionate, Cocamidopropyl Betaine, Sodium Lauroyl Sarcosinate, Glycerin, Decyl Glucoside, Panthenol, Ceramide NP, Ceramide AP, Ceramide EOP, Cholesterol, Phytosphingosine, Allantoin, Sodium PCA, Tocopheryl Acetate, Polyquaternium-7, Hydroxyethyl Ethylcellulose, Xanthan Gum, Tetramethyl Acetyloctahydronaphthalenes (Iso E Super), Dodecahydro-3a,6,6,9a-Tetramethylnaphtho[2,1-b]furan (Ambroxan), Citric Acid, Sodium Benzoate, Potassium Sorbate, Ethylhexylglycerin, Disodium EDTA, Parfum (Fragrance).',
    format: '300ml Gel',
    regulatory: 'EU 1223/2009 (Cosmetic — rinse-off)',
    access: 'sovereign+',
    formulation: {
      pH: '5.0 ± 0.2',
      cogs: 5.50,
      cogsUnit: '300ml',
      margin: 82, // Margin figures: formulation margin (vs COGS only) / retail margin (vs fully loaded cost incl. packaging, labor, shipping)
      shelfLife: '24 months unopened / 6M PAO',
      packaging: '300ml HDPE/PET, flip-top or pump',
      totalSurfactant: '~18%',
      fragranceLoad: '~1.5%',
      viscosity: '3,000–8,000 cP',
      ingredients: [
        { phase: 'A', name: 'Aqua (Water)', pct: 'q.s.', fn: 'Solvent' },
        { phase: 'A', name: 'Glycerin', pct: '5.00', fn: 'Humectant, barrier support' },
        { phase: 'A', name: 'Sodium PCA', pct: '0.50', fn: 'NMF component' },
        { phase: 'A', name: 'Allantoin', pct: '0.20', fn: 'Skin protectant' },
        { phase: 'A', name: 'Disodium EDTA', pct: '0.10', fn: 'Chelating agent' },
        { phase: 'B', name: 'Sodium Cocoyl Isethionate', pct: '8.00', fn: 'Primary surfactant' },
        { phase: 'B', name: 'Cocamidopropyl Betaine', pct: '5.00', fn: 'Co-surfactant (amphoteric)' },
        { phase: 'B', name: 'Sodium Lauroyl Sarcosinate', pct: '3.00', fn: 'Amino acid surfactant' },
        { phase: 'B', name: 'Decyl Glucoside', pct: '2.00', fn: 'Non-ionic co-surfactant' },
        { phase: 'C', name: 'Panthenol', pct: '0.75', fn: 'Barrier repair' },
        { phase: 'C', name: 'Ceramide NP', pct: '0.10', fn: 'Barrier lipid' },
        { phase: 'C', name: 'Ceramide AP', pct: '0.05', fn: 'Barrier lipid' },
        { phase: 'C', name: 'Ceramide EOP', pct: '0.05', fn: 'Barrier lipid' },
        { phase: 'C', name: 'Cholesterol', pct: '0.15', fn: 'Barrier lipid' },
        { phase: 'C', name: 'Phytosphingosine', pct: '0.05', fn: 'Barrier lipid precursor' },
        { phase: 'C', name: 'Tocopheryl Acetate', pct: '0.10', fn: 'Antioxidant' },
        { phase: 'D', name: 'Iso E Super', pct: '1.20', fn: 'Ghost core (logP 5.65)' },
        { phase: 'D', name: 'Ambroxan', pct: '0.30', fn: 'Ghost core (logP ~5.0)' },
        { phase: 'E', name: 'Polyquaternium-7', pct: '0.30', fn: 'Conditioning polymer' },
        { phase: 'E', name: 'Hydroxyethyl Ethylcellulose', pct: '0.40', fn: 'Thickener' },
        { phase: 'E', name: 'Xanthan Gum', pct: '0.20', fn: 'Rheology modifier' },
        { phase: 'F', name: 'Sodium Benzoate', pct: '0.40', fn: 'Preservative' },
        { phase: 'F', name: 'Potassium Sorbate', pct: '0.20', fn: 'Preservative' },
        { phase: 'F', name: 'Ethylhexylglycerin', pct: '0.30', fn: 'Preservative booster' },
        { phase: 'G', name: 'Citric Acid', pct: 'q.s.', fn: 'pH adjustment' },
      ],
      cogsBreakdown: [
        { item: 'SCI (24g active)', cost: '€0.72' },
        { item: 'CAPB, Sarcosinate, Glucoside', cost: '€0.35' },
        { item: 'Glycerin, Panthenol, Allantoin, NaPCA', cost: '€0.25' },
        { item: 'Ceramides + Cholesterol + Phytosphingosine', cost: '€1.20' },
        { item: 'Iso E Super + Ambroxan', cost: '€0.45' },
        { item: 'PQ-7, thickeners, preservatives, EDTA', cost: '€0.30' },
        { item: 'Packaging', cost: '€1.50' },
        { item: 'Filling + labor', cost: '€0.75' },
      ],
    },
  },
  'Ghost Shampoo': {
    tagline: 'Clean hair. No competition.',
    philosophy: 'Your shampoo puts scent in your hair that fights your fragrance for hours. Ghost Shampoo puts nothing. Sodium Cocoyl Isethionate and Disodium Cocoyl Glutamate — the two mildest anionic surfactants available — clean your scalp without stripping it. Hydrolyzed keratin penetrates damaged hair cortex and temporarily restores tensile strength. Panthenol thickens the fiber and holds moisture. Amodimethicone selectively deposits on damaged cuticle areas — conditioning exactly where it\'s needed. pH 4.5–5.0 closes the cuticle flat for smoothness and shine. EDTA chelates hard water minerals that cause dullness. No fragrance. No masking agents. Your hair smells like nothing — which means Ghost smells like everything.',
    use: 'Apply to wet hair, massage into scalp, rinse thoroughly. Follow with conditioner if needed.',
    inci: 'Aqua (Water), Sodium Cocoyl Isethionate, Disodium Cocoyl Glutamate, Cocamidopropyl Betaine, Coco-Glucoside, Glycerin, Panthenol, Hydrolyzed Keratin, Polyquaternium-10, Amodimethicone (and) C11-15 Pareth-7 (and) Trideceth-12, Guar Hydroxypropyltrimonium Chloride, Sodium PCA, Niacinamide, Piroctone Olamine, Salicylic Acid, Tocopheryl Acetate, Citric Acid, Sodium Benzoate, Potassium Sorbate, Ethylhexylglycerin, Disodium EDTA, Sodium Chloride.',
    format: '250ml',
    note: 'Fragrance-free. No masking agents.',
    regulatory: 'EU 1223/2009 (Cosmetic — rinse-off)',
    access: 'sovereign+',
    formulation: {
      pH: '4.75 ± 0.25',
      cogs: 5.10,
      cogsUnit: '250ml',
      margin: 83, // Margin figures: formulation margin (vs COGS only) / retail margin (vs fully loaded cost incl. packaging, labor, shipping)
      shelfLife: '24 months unopened / 6M PAO',
      packaging: '250ml HDPE/PET, flip-top or pump',
      totalSurfactant: '~18%',
      fragranceLoad: '0% (fragrance-free)',
      viscosity: '2,000–6,000 cP',
      ingredients: [
        { phase: 'A', name: 'Aqua (Water)', pct: 'q.s.', fn: 'Solvent' },
        { phase: 'A', name: 'Glycerin', pct: '3.00', fn: 'Humectant' },
        { phase: 'A', name: 'Sodium PCA', pct: '0.50', fn: 'NMF component' },
        { phase: 'A', name: 'Niacinamide', pct: '0.50', fn: 'Scalp barrier support' },
        { phase: 'A', name: 'Disodium EDTA', pct: '0.15', fn: 'Hard water chelation' },
        { phase: 'B', name: 'Sodium Cocoyl Isethionate', pct: '7.00', fn: 'Primary surfactant' },
        { phase: 'B', name: 'Disodium Cocoyl Glutamate', pct: '4.00', fn: 'Amino acid surfactant' },
        { phase: 'B', name: 'Cocamidopropyl Betaine', pct: '5.00', fn: 'Co-surfactant' },
        { phase: 'B', name: 'Coco-Glucoside', pct: '2.00', fn: 'Non-ionic co-surfactant' },
        { phase: 'C', name: 'Panthenol', pct: '1.00', fn: 'Hair penetrant, fiber thickener' },
        { phase: 'C', name: 'Hydrolyzed Keratin', pct: '0.50', fn: 'Protein repair (500–2000 Da)' },
        { phase: 'C', name: 'Piroctone Olamine', pct: '0.10', fn: 'Gentle antifungal' },
        { phase: 'C', name: 'Salicylic Acid', pct: '0.20', fn: 'Mild exfoliant' },
        { phase: 'C', name: 'Tocopheryl Acetate', pct: '0.10', fn: 'Antioxidant' },
        { phase: 'D', name: 'Polyquaternium-10', pct: '0.50', fn: 'Conditioning polymer' },
        { phase: 'D', name: 'Amodimethicone (emulsion)', pct: '1.00', fn: 'Selective conditioning' },
        { phase: 'D', name: 'Guar Hydroxypropyltrimonium Chloride', pct: '0.30', fn: 'Wet detangling' },
        { phase: 'E', name: 'Sodium Benzoate', pct: '0.40', fn: 'Preservative' },
        { phase: 'E', name: 'Potassium Sorbate', pct: '0.20', fn: 'Preservative' },
        { phase: 'E', name: 'Ethylhexylglycerin', pct: '0.30', fn: 'Preservative booster' },
        { phase: 'F', name: 'Sodium Chloride', pct: '0.50', fn: 'Viscosity adjustment' },
        { phase: 'F', name: 'Citric Acid', pct: 'q.s.', fn: 'pH adjustment' },
      ],
      cogsBreakdown: [
        { item: 'SCI (17.5g active)', cost: '€0.53' },
        { item: 'Disodium Cocoyl Glutamate (10g active)', cost: '€0.80' },
        { item: 'CAPB + Coco-Glucoside', cost: '€0.25' },
        { item: 'Glycerin, Panthenol, Niacinamide, NaPCA', cost: '€0.30' },
        { item: 'Hydrolyzed Keratin (low MW)', cost: '€0.35' },
        { item: 'Amodimethicone + PQ-10 + Guar', cost: '€0.45' },
        { item: 'Piroctone Olamine + Salicylic Acid', cost: '€0.10' },
        { item: 'Preservatives, EDTA, NaCl, Citric Acid', cost: '€0.15' },
        { item: 'Packaging', cost: '€1.40' },
        { item: 'Filling + labor', cost: '€0.75' },
      ],
    },
  },
}

// Internal gift catalogue — never rendered publicly. Fulfilment picks from this
// based on tier, season, or promo. Customers discover them when they open the box.
export const giftCatalogue = [
  {
    id: 'canvas',
    name: 'Canvas',
    description: 'Skin primer — molecular reset before fragrance.',
    format: '5ml Roller',
  },
  {
    id: 'binchotan',
    name: 'Kishu Binchotan',
    description: 'Hand-fired Ubame oak white charcoal from Wakayama Prefecture. Regulates humidity, neutralizes ambient odor, purifies the air inside your fragrance case. Recharge quarterly in boiling water. Replace annually.',
    material: 'Ubame Oak (Quercus phillyraeoides), kiln-fired at 1000°C+',
    weight: '~75g stick',
    lifespan: '12 months (recharge every 3 months)',
  },
  {
    id: 'humidity-sachet',
    name: 'Conservation-Grade Humidity Sachet',
    description: 'Museum-grade molecular sieve in branded linen envelope. Protects your collection from moisture, oxidation, and environmental contamination.',
    material: 'Synthetic zeolite (3A molecular sieve) in linen sachet',
    weight: '10g',
    lifespan: '6 months (regenerate by heating at 200°F for 30 minutes)',
  },
  {
    id: 'silence-sample',
    name: 'Silence',
    description: 'Fabric neutralizer sample — 15ml spray.',
    format: '15ml Spray',
  },
  {
    id: 'zero-sample',
    name: 'Zero',
    description: 'Neutral deodorant sample — 10ml stick.',
    format: '10ml Stick',
  },
]

// Physical card copy — ships with each Diagnostic Set vial
// Not note pyramids. Context. Mood. Intent. Like background music in a room.
export const diagnosticCards = {
  Ghost: {
    line: 'This is who you are.',
    mood: 'The room before anyone enters it. Clean air with something underneath — warmth you can\'t place, presence you can\'t name. Ghost doesn\'t announce. It lingers in the space between noticing and remembering. Wear it the way silence fills a room: so naturally no one thinks about it until it\'s gone.',
    when: 'Every day. Every room. Always on.',
  },
  Predator: {
    line: 'When you stop pretending.',
    mood: 'The moment a room rearranges itself around you. Same foundation, different gravity — everything thickened until the air has weight. Nobody touched the dial but the music just got louder. Not for blending in. For the night when you walk in and it\'s already over.',
    when: 'When you dress up and the energy shifts.',
  },
  Euphoria: {
    line: 'In a lawless country.',
    mood: 'The first hour of a night that goes somewhere. Bright, warm, pulling forward — a city at dusk when the lights come on and everything feels possible. Stays with you all night by design. Not for standing still. For the version of the night you\'ll talk about later.',
    when: 'Going out. The night that starts with intent.',
  },
  Intimacy: {
    line: 'When you let go.',
    mood: 'When the room gets smaller and the music drops to a murmur. Oil, not spray — close range only. This doesn\'t project, it invites. The same warmth as Ghost turned inward and softened until the only way to find it is to come closer. You\'ll know when to wear it. So will they.',
    when: 'One on one. When you let someone in.',
  },
  Animal: {
    line: 'When nobody is watching.',
    mood: 'The version without the edit. Warm skin, not perfume — something underneath the civilized layer that reads like you, only more. Oil on pulse points. The kind of warmth people lean into before they realize they\'re leaning. For when polite stops being the point.',
    when: 'Behind closed doors. When nobody is watching.',
  },
  Aftermath: {
    line: 'When it all comes together.',
    mood: 'The final layer over Intimacy. Not a separate scent — the same warmth pushed past the point of return. Oil on skin, skin on skin. The smallest bottle because the moment doesn\'t need volume. It needs heat.',
    when: 'She already knows.',
  },
}

export const stickyCart = {
  label: 'The Diagnostic Set',
  price: 'from €65',
  href: '/login',
}
